import React, { useState } from "react";
import PropTypes from "prop-types";
import { Label, Input, Button, makeStyles, Image, Link } from "@fluentui/react-components";
import config from "../services/config";
import { shorthands } from "@griffel/react";
import {
  ArrowForwardDownLightningRegular,
  ChatRegular,
  ClipboardTextEditRegular,
  ContentViewGalleryLightningRegular,
  FlashRegular,
  HeartFilled,
  HeartRegular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "24px",
    gap: "13px",
    position: "relative",
    width: "auto",
    height: "100%", // Changed from '642px' to '100%'
    background: "#FFFFFF", // white
    borderRadius: "8px",
    alignSelf: "stretch",
    flexGrow: 1, // Allows the component to grow and fill available space
  },
  heading: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px",
    gap: "15px",
    width: "auto",
    height: "auto",
    flex: "none",
    order: 0,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  headingUpper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "6px 0px",
    gap: "10px",
    width: "auto",
    height: "154px",
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  headingTitle: {
    width: "169px",
    height: "52px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "40px",
    lineHeight: "52px",
    textAlign: "center",
    color: "#626262",
    flex: "none",
    order: 1,
    flexGrow: 0,
  },
  discoverMessage: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0px",
    gap: "7px",
    width: "251px",
    height: "55px",
    flex: "none",
    order: 1,
    flexGrow: 0,
  },
  discoverMessageText1: {
    width: "251px",
    height: "28px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "28px", // identical to box height, or 140%
    textAlign: "center",
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  discoverMessageText2: {
    width: "227px",
    height: "20px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "20px", // identical to box height, or 143%
    textAlign: "center",
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 1,
    flexGrow: 0,
  },
  benefitsSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: "0px",
    gap: "6px",
    width: "230px",
    height: "234px",
    flex: "none",
    order: 2,
    flexGrow: 0,
  },
  benefit: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "0px",
    gap: "9px",
    width: "230px",
    maxWidth: "230px",
    height: "54px",
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  benfitIcon: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    padding: "0px",
    width: "20px",
    height: "20px",
    borderRadius: "4px",
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  benefitTextSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "0px",
    gap: "2px",
    width: "201px",
    height: "54px",
    order: 1,
    flex: "1 0 auto",
  },
  benefitText1: {
    width: "176px",
    height: "20px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px", // identical to box height, or 143%
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 0,
    flexGrow: 0,
  },
  benefitText2: {
    width: "201px",
    maxWidth: "906px",
    height: "32px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 400,
    fontSize: "12px",
    lineHeight: "16px", // or 133%
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 1,
    alignSelf: "stretch",
    flexGrow: 0,
  },
  customerTypesLabel: {
    width: "210px",
    height: "14px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "14px", // identical to box height, or 140%
    textAlign: "center",
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 1,
    flexGrow: 0,
  },
  getStartedButton: {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "12px",
    width: "115px",
    height: "40px",
    background: "#74B1B1", // themeTertiary
    border: "1px solid #468D8D", // themeSecondary
    borderRadius: "8px",
    flex: "none",
    order: 2,
    flexGrow: 0,
  },
  disclaimerLabel: {
    width: "137px",
    height: "28px",
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "14px", // or 140%
    textAlign: "center",
    color: "#626262", // neutralPrimaryAlt
    flex: "none",
    order: 3,
    flexGrow: 0,
  },
});

const WelcomeScreen = ({ onGettingStartedClick, addinType }) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.heading}>
        <div className={classes.headingUpper}>
          <Image src="assets/logo_orange.svg" className={classes.logo} />
          <Label className={classes.headingTitle}>Welcome</Label>
        </div>
        <div className={classes.discoverMessage}>
          <Label className={classes.discoverMessageText1}>
            Discover Unicli for {addinType == "OUTLOOK" ? "Outlook" : "Word"}.
          </Label>
          <Label className={classes.discoverMessageText2}>Empower Property Managers with AI</Label>
        </div>
        <div className={classes.benefitsSection}>
          <div className={classes.benefit}>
            <ClipboardTextEditRegular className={classes.benfitIcon} color="#357C7C" />
            <div className={classes.benefitTextSection}>
              <Label className={classes.benefitText1}>
                {addinType == "OUTLOOK" ? "Compose and Reply with AI" : "Minutes in Minutes"}
              </Label>
              <Label className={classes.benefitText2}>
                {addinType == "OUTLOOK"
                  ? "Draft professional emails, while remaining in complete control."
                  : "Draft and refine professional content, while remaining in complete control."}
              </Label>
            </div>
          </div>
          <div className={classes.benefit}>
            <ChatRegular className={classes.benfitIcon} color="#357C7C" />
            <div className={classes.benefitTextSection}>
              <Label className={classes.benefitText1}>
                {addinType == "OUTLOOK" ? "Instant Customer Insight" : "Instant Customer Insight"}
              </Label>
              <Label className={classes.benefitText2}>
                {addinType == "OUTLOOK"
                  ? "Chat with Unicli to get instant insight about your customers."
                  : "Chat with Unicli to get instant insight about your customers."}
              </Label>
            </div>
          </div>
          <div className={classes.benefit}>
            <FlashRegular className={classes.benfitIcon} color="#357C7C" />
            <div className={classes.benefitTextSection}>
              <Label className={classes.benefitText1}>
                {addinType == "OUTLOOK" ? "Increase Efficiency" : "Increase Efficiency"}
              </Label>
              <Label className={classes.benefitText2}>
                {addinType == "OUTLOOK"
                  ? "Instantly identify customer context and relevant documentation."
                  : "Instantly incorporate customer context and relevant documentation."}
              </Label>
            </div>
          </div>
          <div className={classes.benefit}>
            <HeartRegular className={classes.benfitIcon} color="#357C7C" />
            <div className={classes.benefitTextSection}>
              <Label className={classes.benefitText1}>
                {addinType == "OUTLOOK" ? "Improve Client Satisfaction" : "Improve Client Satisfaction"}
              </Label>
              <Label className={classes.benefitText2}>
                {addinType == "OUTLOOK"
                  ? "Impress your clients by staying knowledgeable, and responsive."
                  : "Impress your clients by being knowledgeable, and expedient."}
              </Label>
            </div>
          </div>
        </div>
      </div>
      <Label className={classes.customerTypesLabel}>Strata | Condominium | HOA | Body Corporate</Label>
      <Button onClick={onGettingStartedClick} className={classes.getStartedButton}>
        Get Started
      </Button>
      <Label className={classes.disclaimerLabel}>
        Unicli requires a subscription. Learn more at <a href="https://unicli.ai">unicli.ai</a>
      </Label>
    </div>
  );
};

WelcomeScreen.propTypes = {
  onGettingStartedClick: PropTypes.func.isRequired,
  addinType: PropTypes.string,
};
export default WelcomeScreen;
